import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { firstValueFrom, Observable, take } from 'rxjs';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import {
  SystemTypeEnum,
  SystemTypeIdEnum,
} from 'src/app/domain/registration/enums/system-type.enum';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { TypeComponentFieldEnum } from 'src/app/domain/registration/enums/type-component-dynamic.enum';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { PermissionService } from 'src/app/shared/permission.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { PartnerIndexSelectComponent } from '../../partners/crud-index/crud-index-select.component';
import { CustomerParcelService } from '../../shared/generate-parcels/crud-service';
import { SystemIndexSelectComponent } from '../../systems/crud-index/crud-index-select.component';
import { TenantService } from '../../tenants/crud-service';
import { CustomerService } from '../crud-service';
import { CustomerPlansSystemModulesComponent } from '../customer-plan-items/customer-plans-system-modules/customer-plans-system-modules.component';
import { CustomerCrudIndex } from './crud-index';
import { ContractsService } from '../../contracts/crud-service';
import { CustomerContractGenerationService } from '../../contracts/services/customer-contract-generation.service';

@Component({
  selector: 'app-customer-index',
  templateUrl: './crud-index.component.html',
  styleUrls: ['./crud-index.component.sass'],
  providers: [CustomerService, TenantService, CustomerParcelService, PermissionService],
})
export class CustomerIndexComponent extends CustomerCrudIndex implements OnInit, OnDestroy {
  override filterFormValues: any = null;

  constructor(
    public override dialog: MatDialog,
    public override service: CustomerService,
    public override contractsService: ContractsService,
    public override toastService: ToastService,
    public override mediaObserver: MediaObserver,
    public override router: Router,
    public override tenantService: TenantService,
    public override customerParcelService: CustomerParcelService,
    public override permissionService: PermissionService,
    public override cdr: ChangeDetectorRef,
    public override customerContractGenerationService: CustomerContractGenerationService,
    private route: ActivatedRoute
  ) {
    super(
      dialog,
      service,
      contractsService,
      toastService,
      mediaObserver,
      router,
      tenantService,
      customerParcelService,
      permissionService,
      cdr,
      customerContractGenerationService
    );
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      const preserveFilter = params['preserveFilter'] === 'true';

      if (preserveFilter) {
        const storedFilter = localStorage.getItem('customerLastAppliedFilter');
        const storedIsActive = localStorage.getItem('customerIsAdvancedFilterActive');
        const storedParams = localStorage.getItem('customerParams');
        const storedFormValues = localStorage.getItem('customerFilterFormValues');

        if (storedFilter && storedIsActive && storedParams) {
          this.lastAppliedFilter = JSON.parse(storedFilter);
          this.isAdvancedFilterActive = storedIsActive === 'true';
          this.params = JSON.parse(storedParams);

          // Restaurar os valoes do formulário
          if (storedFormValues) {
            this.filterFormValues = JSON.parse(storedFormValues);
          }

          CustomerService.lastAppliedFilter = this.lastAppliedFilter;
          CustomerService.isAdvancedFilterActive = this.isAdvancedFilterActive;
          CustomerService.params = this.params;
        } else {
          this.isAdvancedFilterActive = CustomerService.isAdvancedFilterActive;
          this.lastAppliedFilter = CustomerService.lastAppliedFilter;
          this.params = CustomerService.params;
        }
      } else {
        localStorage.removeItem('customerLastAppliedFilter');
        localStorage.removeItem('customerIsAdvancedFilterActive');
        localStorage.removeItem('customerParams');
        localStorage.removeItem('customerFilterFormValues');

        CustomerService.isAdvancedFilterActive = false;
        CustomerService.lastAppliedFilter = null;
        CustomerService.params = { limit: 25, page: 0, filter: '' };

        this.params = { limit: 25, page: 0, filter: '' };
        this.isAdvancedFilterActive = false;
        this.lastAppliedFilter = null;
        this.filterFormValues = null;
      }

      this.loadStates();
      this.loadList();
    });
  }

  ngOnDestroy(): void {
    CustomerService.isNavigatingToNew = false;
  }

  override filter(value: string): void {
    this.params.filter = value;

    localStorage.setItem('customerParams', JSON.stringify(this.params));
    CustomerService.params = this.params;

    if (this.isAdvancedFilterActive && this.lastAppliedFilter) {
      this.submitAdvancedFilter({ value: this.lastAppliedFilter } as any);
    } else {
      this.loadList();
    }
  }

  private getDefaultDateValue = () => moment().startOf('day').toDate();

  override filterFields: IFieldConfig[] = [
    {
      type: TypeComponentFieldEnum.INPUT,
      label: 'CÓDIGO INTERNO',
      name: 'code',
      value: '',
    },
    {
      type: TypeComponentFieldEnum.INPUT,
      label: 'CÓDIGO EXTERNO',
      name: 'externalCode',
      value: '',
    },
    {
      type: TypeComponentFieldEnum.SLIDE_TOGGLE,
      label: 'BLOQUEADO',
      name: 'isLock',
      value: false,
    },
    {
      type: TypeComponentFieldEnum.SLIDE_TOGGLE,
      label: 'ATIVO',
      name: 'isActive',
      value: true,
    },
    {
      type: TypeComponentFieldEnum.SELECT_CRUD,
      label: 'PARCEIRO',
      component: PartnerIndexSelectComponent,
      componentData: { params: { isPartner: true } },
      name: 'partner',
      value: '',
    },
    {
      type: TypeComponentFieldEnum.SELECT_CRUD,
      label: 'CONSULTOR',
      component: PartnerIndexSelectComponent,
      name: 'consultant',
      value: '',
    },
    {
      type: TypeComponentFieldEnum.SELECT_CRUD,
      label: 'REPRESENTANTE',
      component: PartnerIndexSelectComponent,
      componentData: { params: { isRepresentative: true } },
      name: 'representative',
      value: '',
    },
    {
      type: TypeComponentFieldEnum.SELECT_CRUD,
      label: 'CONTADOR',
      component: PartnerIndexSelectComponent,
      componentData: { params: { isCounter: true } },
      name: 'counter',
      value: '',
    },
    {
      type: TypeComponentFieldEnum.SELECT_CRUD,
      label: 'UNIDADE',
      component: PartnerIndexSelectComponent,
      componentData: { params: { isUnit: true } },
      name: 'unit',
      value: '',
    },
    {
      type: TypeComponentFieldEnum.SELECT_CRUD,
      label: 'SISTEMA',
      name: 'system',
      component: SystemIndexSelectComponent,
      componentData: {
        params: { type: SystemTypeIdEnum[SystemTypeEnum.SYSTEM] },
      },
      value: '',
    },
    {
      type: TypeComponentFieldEnum.SELECT_CRUD,
      label: 'APLICAÇÃO',
      name: 'application',
      component: SystemIndexSelectComponent,
      componentData: {
        params: { type: SystemTypeIdEnum[SystemTypeEnum.APPLICATION] },
      },
      value: '',
    },
    {
      type: TypeComponentFieldEnum.SELECT,
      label: 'ESTADO',
      name: 'uf',
      placeholder: 'Insira a UF do estado',
      onSelect: (value: any) => {
        this.onChangeState(value);
      },
      options: [],
      validation: [],
    },
    {
      type: TypeComponentFieldEnum.SELECT,
      label: 'CIDADE',
      name: 'city',
      placeholder: 'Insira o nome da cidade',
      options: [],
      validation: [],
    },
    {
      type: TypeComponentFieldEnum.INPUT_DATETIME,
      label: 'DATA DE CADASTRO INICIAL',
      name: 'startCreatedAt',
      placeholder: 'Selecione a data',
      value: '',
    },
    {
      type: TypeComponentFieldEnum.INPUT_DATETIME,
      label: 'DATA DE CADASTRO FINAL',
      name: 'endCreatedAt',
      placeholder: '',
      value: this.getDefaultDateValue(),
    },
  ];

  override resetFilter(): void {
    this.filterFormValues = null;
    localStorage.removeItem('customerFilterFormValues');

    this.isAdvancedFilterActive = false;
    this.lastAppliedFilter = null;
    this.params = { limit: 25, page: 0, filter: '' };

    localStorage.removeItem('customerParams');
    localStorage.removeItem('customerIsAdvancedFilterActive');
    localStorage.removeItem('customerLastAppliedFilter');

    CustomerService.isAdvancedFilterActive = false;
    CustomerService.lastAppliedFilter = null;
    CustomerService.params = { limit: 25, page: 0, filter: '' };

    const resetFields = this.filterFields.map(field => {
      const fieldCopy = { ...field };

      if (field.type === TypeComponentFieldEnum.SLIDE_TOGGLE) {
        if (field.name === 'isActive') {
          fieldCopy.value = true;
        } else {
          fieldCopy.value = false;
        }
      }
      else if (field.type === TypeComponentFieldEnum.SELECT ||
        field.type === TypeComponentFieldEnum.SELECT_CRUD) {
        fieldCopy.value = '';
      }
      else {
        fieldCopy.value = '';
      }

      return fieldCopy;
    });

    this.filterFields = resetFields;
    this.loadList(true);
    this.cdr.detectChanges();
  }

  override loadList(isReset: boolean = false): void {
    if (isReset) {
      super.loadList();
      return;
    }

    const storedParams = localStorage.getItem('customerParams');
    const storedFilter = localStorage.getItem('customerLastAppliedFilter');
    const storedIsActive = localStorage.getItem('customerIsAdvancedFilterActive');

    if (storedParams) {
      this.params = JSON.parse(storedParams);
      CustomerService.params = this.params;
    }

    if (storedIsActive === 'true' && storedFilter) {
      this.isAdvancedFilterActive = true;
      this.lastAppliedFilter = JSON.parse(storedFilter);
      this.submitAdvancedFilter({ value: this.lastAppliedFilter } as any);
    } else {
      super.loadList();
    }
  }

  override changePage(event: any) {
    this.params.page = event.page;
    this.params.limit = event.limit;

    localStorage.setItem('customerParams', JSON.stringify(this.params));
    CustomerService.params = this.params;

    if (this.isAdvancedFilterActive && this.lastAppliedFilter) {
      this.submitAdvancedFilter({ value: this.lastAppliedFilter } as any);
    } else {
      this.loadList();
    }
  }

  override submitAdvancedFilter(filterForm: FormGroup) {
    let _filterForm = this.getFilters(filterForm.value);

    this.filterFormValues = filterForm.value;
    localStorage.setItem('customerFilterFormValues', JSON.stringify(this.filterFormValues));
    localStorage.setItem('customerLastAppliedFilter', JSON.stringify(_filterForm));
    localStorage.setItem('customerIsAdvancedFilterActive', 'true');
    localStorage.setItem('customerParams', JSON.stringify(this.params));
    CustomerService.lastAppliedFilter = { ..._filterForm };
    CustomerService.isAdvancedFilterActive = true;

    this.service
      .findByFilter(_filterForm, this.params)
      .pipe(take(1))
      .subscribe((dataWithPagination: any) => {
        const [data, length, actives, deactives, locks] = dataWithPagination;
        this.length = length;
        this.totalizers = {
          ...this.totalizers,
          registrationTotal: length,
          activeTotal: actives,
          deactiveTotal: deactives,
          lockTotal: locks,
        };

        // Prepara os dados iniciais
        const initialData = data.map((item: any) => {
          item['document'] = item.cnpj || item.cpf;
          item['name'] = item.companyName || item.name;
          item['isLicenseGenerated'] = item.tenantId !== null;
          item['hideEdit'] = !item.allowEdit;
          item['hideDelete'] = !item.allowDelete;
          item.code = `${item.code}`.padStart(4, '0');
          item['isContractSigned'] = 'LOADING';
          item['isContractGenerated'] = 'LOADING';

          return item;
        });

        this.dataSource = new MatTableDataSource<any>(initialData);
        this.applyClientSideFilter();
        this.updateContractStatus();
      });
  }

  protected override applyClientSideFilter(): void {
    if (!this.dataSource) return;

    if (this.params.filter && this.params.filter.trim() !== '') {
      const filterValue = this.params.filter.toLowerCase();
      this.dataSource.filter = filterValue;
      this.dataSource.filterPredicate = (data: any, filter: string) => {
        const name = (data.name || '').toLowerCase();
        const document = (data.document || '').toLowerCase();
        const email = (data.email || '').toLowerCase();
        const code = (data.code || '').toLowerCase();
        const isContractGenerated = (data.isContractGenerated || '').toLowerCase();
        const isContractSigned = (data.isContractSigned || '').toLowerCase();

        return name.includes(filter) ||
          document.includes(filter) ||
          email.includes(filter) ||
          code.includes(filter) ||
          isContractGenerated.includes(filter) ||
          isContractSigned.includes(filter);
      };
    }
  }

  protected updateContractStatus(): void {
    if (!this.dataSource?.data || this.dataSource.data.length === 0) return;

    const promises = this.dataSource.data.map(async (item) => {
      try {
        const contractStatus = await firstValueFrom(this.contractsService.getContractsByCustomer(item.id));

        if (contractStatus && Array.isArray(contractStatus)) {
          const totalContracts = contractStatus.length;
          const signedContracts = contractStatus.filter(c => c.status === 'signed').length;

          if (totalContracts > 0) {
            if (signedContracts === totalContracts) {
              item['isContractSigned'] = 'SIM';
            } else if (signedContracts > 0) {
              item['isContractSigned'] = `PARCIAL (${signedContracts}/${totalContracts})`;
            } else {
              item['isContractSigned'] = 'NÃO';
            }
          } else {
            item['isContractSigned'] = 'NÃO';
          }

          item['isContractGenerated'] = totalContracts > 0 ? 'SIM' : 'NÃO';
        } else {
          item['isContractSigned'] = 'NÃO';
          item['isContractGenerated'] = 'NÃO';
        }
      } catch (error) {
        console.error(`Erro ao verificar contratos do cliente ${item.id}:`, error);
        item['isContractSigned'] = 'ERRO';
        item['isContractGenerated'] = 'ERRO';
      }

      return item;
    });

    Promise.all(promises).then(processedData => {
      this.dataSource.data = processedData;
      this.applyClientSideFilter();
      this.cdr.detectChanges();
    });
  }

  private getFilters(filter: any) {
    let params: any = {};

    if (filter.code) params.code = filter.code;
    else delete params.code;

    if (filter.externalCode) params.externalCode = filter.externalCode;
    else delete params.externalCode;

    if (filter.hasOwnProperty('isLock')) {
      // Só envia isLock se o filtro for explicitamente aplicado pelo usuário
      if (filter.isLock !== undefined && filter.isLock !== null && filter.isLock !== '') {
        params.isLock = filter.isLock;
      } else {
        delete params.isLock;
      }
    }

    if (filter.hasOwnProperty('isActive')) {
      params.isActive = filter.isActive;
    } else {
      delete params.isActive;
    }

    if (filter.partner && filter.partner !== 'TODOS')
      params.partner = filter.partner.id;
    else delete params.partner;

    if (filter.consultant && filter.consultant !== 'TODOS')
      params.consultant = filter.consultant.id;
    else delete params.consultant;

    if (filter.representative && filter.representative !== 'TODOS')
      params.representative = filter.representative.id;
    else delete params.representative;

    if (filter.counter && filter.counter !== 'TODOS')
      params.counter = filter.counter.id;
    else delete params.counter;

    if (filter.unit && filter.unit !== 'TODOS')
      params.unit = filter.unit.id;
    else delete params.unit;

    if (filter.system && filter.system !== 'TODOS')
      params.system = filter.system.id;
    else delete params.system;

    if (filter.application && filter.application !== 'TODOS')
      params.application = filter.application.id;
    else delete params.application;

    if (filter.city && filter.city !== 'TODOS') params.city = filter.city;
    else delete params.city;

    if (filter.uf && filter.uf !== 'TODOS') params.uf = filter.uf;
    else delete params.uf;

    if (filter.startCreatedAt && filter.endCreatedAt) {
      const endDate = moment(filter.endCreatedAt).endOf('day').toDate();
      params.createdAtBetween = {
        start: filter.startCreatedAt,
        end: endDate
      };
      delete params.startCreatedAt;
      delete params.endCreatedAt;
    } else {
      delete params.createdAtBetween;
      if (filter.startCreatedAt) {
        params.startCreatedAt = filter.startCreatedAt;
      } else {
        delete params.startCreatedAt;
      }
      if (filter.endCreatedAt) {
        params.endCreatedAt = moment(filter.endCreatedAt).endOf('day').toDate();
      } else {
        delete params.endCreatedAt;
      }
    }

    return params;
  }

  public getFieldConfig(name: string): any {
    return this.filterFields.find((c) => c.name === name);
  }

  public loadStates() {
    this.service
      .getStates()
      .pipe(take(1))
      .subscribe((resp) => {
        this.getFieldConfig('uf').options = [
          '',
          ...resp.map((r: any) => r.sigla).sort(),
        ];
        this.getFieldConfig('city').value = '';
      });
  }

  public onChangeState(data: string) {
    this.getCitiesByUf(data).subscribe((resp: any) => {
      this.getFieldConfig('city').options = resp.map((v: any) => v.nome);
      this.cdr.detectChanges();
    });
  }

  public getCitiesByUf(uf?: any): Observable<any> {
    const state = uf || this.getFieldConfig('uf')?.value;
    this.getFieldConfig('city').options = [];

    return this.service.getCitiesByUf(state);
  }

  public override _openNew($event: any) {
    CustomerService.isAdvancedFilterActive = this.isAdvancedFilterActive;
    CustomerService.lastAppliedFilter = this.lastAppliedFilter;
    CustomerService.params = this.params;
    CustomerService.isNavigatingToNew = true;

    this.router.navigate(['customers/new']);
  }

  public override moneyAction(element: any) {
    this.router.navigate(['accounts-receivable/show', element.id]);
  }

  public override viewClientPlans(element: any) {
    this.service.get(element.id).subscribe((customer: any) => {
      if (customer && customer.customerPlans) {
        const formattedPlans = customer.customerPlans.map((plan: any) => {
          return {
            ...plan,
            type: this.getSystemTypeDescription(plan.type),
            system: typeof plan.system === 'object' ? plan.system.name : plan.system,
            systemModules: Array.isArray(plan.systemModules) ? plan.systemModules : [],
            customerPlanAudits: Array.isArray(plan.customerPlanAudits) ? plan.customerPlanAudits : []
          };
        });

        // Abrir o diálogo com os planos formatados
        GlobalUtils.openDialog({
          dialog: this.dialog,
          data: {
            title: `Planos do Cliente: ${element.name || element.companyName}`,
            element: {
              customerPlans: formattedPlans
            }
          },
          component: CustomerPlansSystemModulesComponent,
          dialogConfig: {
            width: '90%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            panelClass: 'customer-plans-dialog'
          }
        });
      } else {
        this.toastService.showInfo('Cliente não possui planos cadastrados.');
      }
    });
  }

  // Método para converter o tipo de sistema de ID para descrição
  private getSystemTypeDescription(typeId: number): string {
    const types: { [key: number]: string } = {
      1: SystemTypeEnum.SYSTEM,
      2: SystemTypeEnum.APPLICATION,
      3: SystemTypeEnum.MARKETING,
      4: SystemTypeEnum.CLOUD,
      5: SystemTypeEnum.REVENDA,
      6: SystemTypeEnum.SERVICES,
      7: SystemTypeEnum.CONSULTANCY,
      8: SystemTypeEnum.ACCOUNTING,
    };

    return types[typeId] || 'Desconhecido';
  }
}
